import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useToast } from '../../ui/Toast';
import { PageHead } from '../../shell/PageHead';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Modal } from '../../ui/Modal';
import { Icon, type IconName } from '../../ui/Icon';

interface FileItem {
  id: string;
  company_id: string;
  company_nome: string | null;
  tipo: string;
  extensao: string;
  mime: string;
  tamanho_bytes: number;
  descricao: string | null;
  human_revision: boolean;
  origem: string;
  criada_em: string;
  revisada_em: string | null;
  deleted_at: string | null;
  purge_after: string | null;
  keywords: string[];
}

type TipoFilter = 'all' | 'image' | 'video' | 'audio' | 'document' | 'other';
type RevisionFilter = 'all' | 'true' | 'false';
type View = 'active' | 'trash';

const TIPO_ICONS: Record<string, IconName> = {
  image: 'image',
  video: 'film',
  audio: 'volume',
  document: 'book',
  presentation: 'columns',
  spreadsheet: 'grid',
  data: 'database',
  other: 'database'
};

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(iso: string) {
  const diff = new Date(iso).getTime() - Date.now();
  const future = diff > 0;
  const abs = Math.abs(diff);
  const min = Math.floor(abs / 60000);
  const fmt = (n: number, u: string) => (future ? `em ${n}${u}` : `${n}${u}`);
  if (min < 1) return future ? 'em instantes' : 'agora';
  if (min < 60) return fmt(min, 'm');
  const h = Math.floor(min / 60);
  if (h < 24) return fmt(h, 'h');
  const d = Math.floor(h / 24);
  if (d < 60) return fmt(d, 'd');
  return new Date(iso).toLocaleDateString('pt-BR');
}

/** Player inline — usa a URL pública de download (ADR-0018). */
function FileViewer({ f }: { f: FileItem }) {
  const url = `/v1/files/${f.id}/download`;
  // Checa se o blob físico existe antes de renderizar o player (evita iframe/img
  // mostrando JSON de 404 para arquivos sem blob — ex.: demo:seed antigos).
  const [status, setStatus] = useState<'checking' | 'ok' | 'missing'>('checking');
  useEffect(() => {
    let alive = true;
    setStatus('checking');
    fetch(url, { method: 'HEAD' })
      .then((r) => alive && setStatus(r.ok ? 'ok' : 'missing'))
      .catch(() => alive && setStatus('missing'));
    return () => {
      alive = false;
    };
  }, [url]);

  if (status === 'checking') {
    return <div className="skeleton" style={{ height: 160, borderRadius: 'var(--r-md)' }} />;
  }
  if (status === 'missing') {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: 'var(--text-2)',
          fontSize: 13.5,
          border: '1px dashed var(--border)',
          borderRadius: 'var(--r-md)',
          background: 'var(--bg-2)'
        }}
      >
        Pré-visualização indisponível — arquivo sem blob físico no storage.
      </div>
    );
  }

  const box: CSSProperties = {
    width: '100%',
    maxHeight: '46vh',
    borderRadius: 'var(--r-md)',
    border: '1px solid var(--border)',
    background:
      'repeating-conic-gradient(var(--bg-2) 0% 25%, var(--bg-1) 0% 50%) 50% / 20px 20px',
    objectFit: 'contain',
    display: 'block'
  };
  if (f.tipo === 'image')
    return <img src={url} alt={f.descricao ?? 'arquivo'} style={{ ...box, minHeight: 120 }} />;
  if (f.tipo === 'video')
    return <video src={url} controls style={{ ...box, minHeight: 160 }} />;
  if (f.tipo === 'audio')
    return <audio src={url} controls style={{ width: '100%' }} />;
  if (f.tipo === 'document' && f.extensao.toLowerCase() === 'pdf')
    return <iframe src={url} title={f.id} style={{ width: '100%', height: '46vh', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }} />;
  return (
    <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 13.5 }}>
      Sem preview inline para <b>.{f.extensao}</b>.{' '}
      <a href={`${url}?dl=1`} target="_blank" rel="noreferrer" style={{ color: 'var(--brand)' }}>
        Baixar arquivo
      </a>
    </div>
  );
}

export function Files() {
  const { apiFetch, user, impersonate } = useAuth();
  const toast = useToast();

  const [items, setItems] = useState<FileItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const busyRef = useRef(false); // guarda síncrona contra duplo-clique

  const [view, setView] = useState<View>('active');
  const [tipo, setTipo] = useState<TipoFilter>('all');
  const [rev, setRev] = useState<RevisionFilter>('all');
  const [q, setQ] = useState('');

  const [detail, setDetail] = useState<FileItem | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<FileItem | null>(null);
  const [purgeWord, setPurgeWord] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkWord, setBulkWord] = useState('');
  const [job, setJob] = useState<{ id: string; progress: number; total: number; status: string } | null>(null);
  const jobPoll = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(
    () => () => {
      if (jobPoll.current) clearInterval(jobPoll.current);
    },
    []
  );

  const showCompanyCol = useMemo(
    () => user?.papel === 'super_admin' && !impersonate,
    [user, impersonate]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelected(new Set());
    const params = new URLSearchParams();
    if (tipo !== 'all') params.set('tipo', tipo);
    if (rev !== 'all') params.set('human_revision', rev);
    if (q.trim()) params.set('q', q.trim());
    params.set('include_deleted', view === 'trash' ? 'only' : 'false');
    params.set('limit', '50');
    const r = await apiFetch<{ items: FileItem[]; total: number }>(
      `/v1/admin/files/?${params.toString()}`
    );
    setLoading(false);
    if (!r.ok || !r.data) {
      setError(r.error?.message ?? 'Falha ao carregar');
      return;
    }
    setItems(r.data.items);
    setTotal(r.data.total);
  }, [apiFetch, tipo, rev, q, view]);

  useEffect(() => {
    load();
  }, [load]);

  function clearFilters() {
    setTipo('all');
    setRev('all');
    setQ('');
  }

  async function withGuard(id: string, fn: () => Promise<boolean>) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(id);
    try {
      await fn();
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }

  function approve(id: string) {
    return withGuard(id, async () => {
      const r = await apiFetch(`/v1/admin/files/${id}`, {
        method: 'PATCH',
        body: { human_revision: true }
      });
      if (!r.ok) {
        toast(r.error?.message ?? 'Falha ao aprovar', 'danger');
        return false;
      }
      toast('Aprovado', 'success');
      setItems((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, human_revision: true, revisada_em: new Date().toISOString() } : f
        )
      );
      return true;
    });
  }

  async function restore(id: string) {
    const r = await apiFetch(`/v1/admin/files/${id}/restore`, { method: 'POST' });
    if (!r.ok) {
      toast(r.error?.message ?? 'Falha ao restaurar', 'danger');
      return;
    }
    toast('Arquivo restaurado', 'success');
    load();
  }

  function softDelete(id: string) {
    return withGuard(id, async () => {
      const r = await apiFetch(`/v1/admin/files/${id}`, { method: 'DELETE' });
      if (!r.ok) {
        toast(r.error?.message ?? 'Falha ao excluir', 'danger');
        return false;
      }
      setItems((prev) => prev.filter((f) => f.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      setDetail(null);
      toast('Movido para lixeira', 'info', {
        actionLabel: 'Desfazer',
        duration: 8000,
        onAction: () => restore(id)
      });
      return true;
    });
  }

  async function doPurge() {
    if (!purgeTarget || purgeWord !== 'PURGAR') return;
    const id = purgeTarget.id;
    const r = await apiFetch(`/v1/admin/files/${id}/purge`, {
      method: 'POST',
      body: { confirm_word: 'PURGAR' }
    });
    if (!r.ok) {
      toast(r.error?.message ?? 'Falha ao purgar', 'danger');
      return;
    }
    toast('Arquivo purgado permanentemente', 'success');
    setItems((prev) => prev.filter((f) => f.id !== id));
    setTotal((t) => Math.max(0, t - 1));
    setPurgeTarget(null);
    setPurgeWord('');
    setDetail(null);
  }

  function toggleSel(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }
  function toggleAll() {
    setSelected((s) => (s.size === items.length ? new Set() : new Set(items.map((f) => f.id))));
  }

  function startPolling(jobId: string, total: number) {
    setJob({ id: jobId, progress: 0, total, status: 'pending' });
    if (jobPoll.current) clearInterval(jobPoll.current);
    jobPoll.current = setInterval(async () => {
      const r = await apiFetch<{ job: { progress: number; total: number; status: string; result_json: { ok?: number; errors?: unknown[] } | null } }>(
        `/v1/admin/jobs/${jobId}`
      );
      if (!r.ok || !r.data) return;
      const j = r.data.job;
      setJob({ id: jobId, progress: j.progress, total: j.total, status: j.status });
      if (['done', 'partial', 'failed', 'cancelled'].includes(j.status)) {
        if (jobPoll.current) clearInterval(jobPoll.current);
        jobPoll.current = null;
        const ok = j.result_json?.ok ?? 0;
        const errs = j.result_json?.errors?.length ?? 0;
        toast(
          j.status === 'cancelled'
            ? 'Job cancelado'
            : `Concluído: ${ok} ok${errs ? `, ${errs} falha(s)` : ''}`,
          errs ? 'info' : 'success'
        );
        setTimeout(() => setJob(null), 1800);
        load();
      }
    }, 2000);
  }

  async function bulkRun(action: 'approve' | 'delete', confirmWord?: string) {
    const ids = [...selected];
    if (ids.length === 0) return;
    const total = ids.length;
    const r = await apiFetch<{ mode: string; result?: { ok: number; errors: unknown[] }; job_id?: string }>(
      '/v1/admin/files/bulk-action',
      { method: 'POST', body: { ids, action, confirm_word: confirmWord } }
    );
    if (!r.ok || !r.data) {
      toast(r.error?.message ?? 'Falha na ação em massa', 'danger');
      return;
    }
    if (r.data.mode === 'sync') {
      const ok = r.data.result?.ok ?? 0;
      const errs = r.data.result?.errors?.length ?? 0;
      toast(`${ok} processado(s)${errs ? `, ${errs} falha(s)` : ''}`, errs ? 'info' : 'success');
      setSelected(new Set());
      load();
    } else if (r.data.job_id) {
      setSelected(new Set());
      startPolling(r.data.job_id, total);
    }
  }

  async function cancelJob() {
    if (!job) return;
    await apiFetch(`/v1/admin/jobs/${job.id}/cancel`, { method: 'POST' });
  }

  const pendingCount = items.filter((f) => !f.human_revision).length;
  const isTrash = view === 'trash';
  const allSelected = items.length > 0 && selected.size === items.length;

  return (
    <>
      <PageHead
        title="Arquivos"
        desc={
          isTrash
            ? 'Lixeira — arquivos excluídos (restauráveis por 30 dias).'
            : impersonate
              ? `Vendo arquivos da empresa "${impersonate.nome}" (impersonate ativo).`
              : showCompanyCol
                ? 'Todos os arquivos do sistema (visão super_admin).'
                : 'Arquivos da sua empresa.'
        }
        actions={
          <>
            <Button
              variant={isTrash ? 'brand' : 'ghost'}
              icon="trash"
              size="sm"
              onClick={() => setView(isTrash ? 'active' : 'trash')}
            >
              {isTrash ? 'Voltar aos ativos' : 'Lixeira'}
            </Button>
            <Button variant="ghost" icon="refresh" size="sm" onClick={load} className="ml-1">
              Atualizar
            </Button>
          </>
        }
      />

      {/* Filtros */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 16,
          padding: '12px 14px',
          background: 'var(--bg-1)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)'
        }}
      >
        <SegmentedFilter
          value={tipo}
          onChange={(v) => setTipo(v as TipoFilter)}
          options={[
            { value: 'all', label: 'Todos' },
            { value: 'image', label: 'Imagens' },
            { value: 'video', label: 'Vídeos' },
            { value: 'audio', label: 'Áudios' },
            { value: 'document', label: 'Documentos' },
            { value: 'other', label: 'Outros' }
          ]}
        />
        {!isTrash && (
          <SegmentedFilter
            value={rev}
            onChange={(v) => setRev(v as RevisionFilter)}
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'true', label: 'Aprovados' },
              { value: 'false', label: 'Pendentes' }
            ]}
          />
        )}
        <input
          name="files-search"
          placeholder="Buscar por descrição ou keyword…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') load();
          }}
          className="focusable"
          style={{
            flex: 1,
            minWidth: 200,
            padding: '6px 10px',
            fontSize: 13,
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--bg-0)',
            color: 'var(--text-0)',
            outline: 'none'
          }}
        />
        <span className="tnum" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
          {total} {isTrash ? 'na lixeira' : 'total'}
          {!isTrash && ` · ${pendingCount} pendentes`}
        </span>
      </div>

      {/* Barra de ação em massa / progresso de job */}
      {!isTrash && (selected.size > 0 || job) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 14,
            padding: '10px 14px',
            background: 'var(--brand-bg, var(--bg-2))',
            border: '1px solid var(--brand)',
            borderRadius: 'var(--r-md)'
          }}
        >
          {job ? (
            <>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-0)' }}>
                Processando {job.progress}/{job.total}…
              </span>
              <div style={{ flex: 1, height: 6, background: 'var(--bg-1)', borderRadius: 4, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${job.total ? Math.round((job.progress / job.total) * 100) : 0}%`,
                    height: '100%',
                    background: 'var(--brand)',
                    transition: 'width 0.3s var(--ease)'
                  }}
                />
              </div>
              <Button variant="ghost" size="sm" onClick={cancelJob}>
                Cancelar
              </Button>
            </>
          ) : (
            <>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-0)', flex: 1 }}>
                {selected.size} selecionado(s)
              </span>
              <Button variant="brand" size="sm" icon="check" onClick={() => bulkRun('approve')}>
                Aprovar
              </Button>
              <Button variant="danger" size="sm" icon="trash" onClick={() => setBulkDeleteOpen(true)}>
                Excluir
              </Button>
              <Button variant="ghost" size="sm" icon="x" onClick={() => setSelected(new Set())}>
                Limpar
              </Button>
            </>
          )}
        </div>
      )}

      {error && (
        <div
          style={{
            background: 'var(--danger-bg)',
            color: 'var(--danger)',
            border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)',
            borderRadius: 'var(--r-sm)',
            padding: '10px 14px',
            fontSize: 13.5,
            marginBottom: 14
          }}
        >
          {error}
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="skeleton" style={{ height: 56 }} />
          <div className="skeleton" style={{ height: 56 }} />
          <div className="skeleton" style={{ height: 56 }} />
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div
          className="grid-bg"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '64px 24px',
            borderRadius: 'var(--r-lg)',
            border: '1px dashed var(--border)',
            gap: 12,
            minHeight: 280,
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 'var(--r-md)',
              background: 'var(--bg-1)',
              border: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-2)'
            }}
          >
            <Icon name={isTrash ? 'trash' : 'image'} size={22} />
          </div>
          <div>
            <h3 style={{ fontSize: 15.5, marginBottom: 6 }}>
              {isTrash ? 'Lixeira vazia' : 'Nenhum arquivo encontrado'}
            </h3>
            <p style={{ fontSize: 13.5, color: 'var(--text-2)' }}>
              {isTrash
                ? 'Arquivos excluídos aparecem aqui por 30 dias.'
                : tipo !== 'all' || rev !== 'all' || q.trim()
                  ? 'Nenhum resultado para os filtros atuais.'
                  : 'Os arquivos chegam quando empresas consomem a API.'}
            </p>
          </div>
          {!isTrash && (tipo !== 'all' || rev !== 'all' || q.trim()) && (
            <Button variant="ghost" size="sm" icon="x" onClick={clearFilters}>
              Limpar filtros
            </Button>
          )}
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div
          style={{
            background: 'var(--bg-1)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-lg)',
            overflow: 'hidden'
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: 'var(--bg-2)', textAlign: 'left' }}>
                {!isTrash && (
                  <th style={{ ...thStyle, width: 40 }}>
                    <input
                      type="checkbox"
                      aria-label="Selecionar todos"
                      checked={allSelected}
                      onChange={toggleAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                <th style={thStyle}>Tipo</th>
                <th style={thStyle}>Descrição</th>
                {showCompanyCol && <th style={thStyle}>Empresa</th>}
                <th style={thStyle}>Keywords</th>
                <th style={thStyle}>Origem</th>
                <th style={thStyle}>{isTrash ? 'Purga em' : 'Status'}</th>
                <th style={thStyle}>Criado</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => {
                const isPending = !f.human_revision;
                const inBusy = busy === f.id;
                return (
                  <tr
                    key={f.id}
                    style={{
                      borderTop: '1px solid var(--border-soft)',
                      opacity: inBusy ? 0.5 : 1,
                      transition: 'opacity 0.18s var(--ease)',
                      background: selected.has(f.id) ? 'var(--bg-2)' : undefined
                    }}
                  >
                    {!isTrash && (
                      <td style={{ padding: '12px 16px' }}>
                        <input
                          type="checkbox"
                          aria-label={`Selecionar ${f.descricao ?? f.id}`}
                          checked={selected.has(f.id)}
                          onChange={() => toggleSel(f.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                    )}
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={() => setDetail(f)}
                        className="focusable"
                        title="Ver detalhe"
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 'var(--r-sm)',
                          background: 'var(--bg-2)',
                          border: '1px solid var(--border)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--text-2)',
                          cursor: 'pointer'
                        }}
                      >
                        <Icon name={TIPO_ICONS[f.tipo] ?? 'database'} size={18} />
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px', maxWidth: 320 }}>
                      <div
                        onClick={() => setDetail(f)}
                        style={{
                          fontWeight: 500,
                          color: 'var(--text-0)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer'
                        }}
                      >
                        {f.descricao ?? <span style={{ color: 'var(--text-3)' }}>Sem descrição</span>}
                      </div>
                      <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                        {f.extensao.toUpperCase()} · {formatBytes(f.tamanho_bytes)}
                      </div>
                    </td>
                    {showCompanyCol && (
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: 12.5, color: 'var(--text-1)', fontWeight: 500 }}>
                          {f.company_nome ?? '—'}
                        </span>
                      </td>
                    )}
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 240 }}>
                        {f.keywords.slice(0, 4).map((k) => (
                          <Badge key={k} color="neutral" size="sm">
                            {k}
                          </Badge>
                        ))}
                        {f.keywords.length > 4 && (
                          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                            +{f.keywords.length - 4}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
                        {f.origem}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {isTrash ? (
                        <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                          {f.purge_after ? formatRelative(f.purge_after) : '—'}
                        </span>
                      ) : isPending ? (
                        <Badge color="warning" dot>
                          pendente
                        </Badge>
                      ) : (
                        <Badge color="success" dot>
                          aprovado
                        </Badge>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                      {formatRelative(f.criada_em)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <a
                        href={`/v1/files/${f.id}/download`}
                        target="_blank"
                        rel="noreferrer"
                        className="focusable"
                        title="Abrir o arquivo (URL direta, sem apikey)"
                        style={openLinkStyle}
                      >
                        Abrir ↗
                      </a>
                      {isTrash ? (
                        <>
                          <Button
                            variant="brand"
                            size="sm"
                            icon="refresh"
                            onClick={() => restore(f.id)}
                            disabled={inBusy}
                            className="ml-1"
                          >
                            Restaurar
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            icon="trash"
                            onClick={() => setPurgeTarget(f)}
                            disabled={inBusy}
                            className="ml-1"
                          >
                            Purgar
                          </Button>
                        </>
                      ) : (
                        <>
                          {isPending && (
                            <Button
                              variant="brand"
                              size="sm"
                              icon="check"
                              onClick={() => approve(f.id)}
                              disabled={inBusy}
                              className="ml-1"
                            >
                              Aprovar
                            </Button>
                          )}
                          <Button
                            variant="danger"
                            size="sm"
                            icon="trash"
                            onClick={() => softDelete(f.id)}
                            disabled={inBusy}
                            className="ml-1"
                          >
                            Excluir
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de detalhe + player inline */}
      <Modal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.descricao ?? 'Arquivo'}
        subtitle={detail ? `${detail.tipo} · ${detail.extensao.toUpperCase()} · ${formatBytes(detail.tamanho_bytes)}` : undefined}
        width={640}
      >
        {detail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FileViewer f={detail} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {detail.keywords.map((k) => (
                <Badge key={k} color="neutral" size="sm">
                  {k}
                </Badge>
              ))}
            </div>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', fontSize: 12.5, margin: 0 }}>
              {showCompanyCol && (
                <>
                  <dt style={dtStyle}>Empresa</dt>
                  <dd style={{ margin: 0 }}>{detail.company_nome ?? '—'}</dd>
                </>
              )}
              <dt style={dtStyle}>Origem</dt>
              <dd style={{ margin: 0 }} className="mono">{detail.origem}</dd>
              <dt style={dtStyle}>Status</dt>
              <dd style={{ margin: 0 }}>{detail.human_revision ? 'aprovado' : 'pendente'}</dd>
              <dt style={dtStyle}>Criado</dt>
              <dd style={{ margin: 0 }}>{new Date(detail.criada_em).toLocaleString('pt-BR')}</dd>
              <dt style={dtStyle}>URL</dt>
              <dd style={{ margin: 0 }} className="mono">/v1/files/{detail.id}/download</dd>
            </dl>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <a href={`/v1/files/${detail.id}/download`} target="_blank" rel="noreferrer" style={openLinkStyle}>
                Abrir ↗
              </a>
              {!detail.deleted_at && !detail.human_revision && (
                <Button variant="brand" size="sm" icon="check" onClick={() => detail && approve(detail.id)}>
                  Aprovar
                </Button>
              )}
              {!detail.deleted_at && (
                <Button variant="danger" size="sm" icon="trash" onClick={() => detail && softDelete(detail.id)}>
                  Excluir
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de purga (confirmação destrutiva) */}
      <Modal
        open={!!purgeTarget}
        onClose={() => {
          setPurgeTarget(null);
          setPurgeWord('');
        }}
        title="Purgar permanentemente"
        danger
        subtitle="Apaga o registro e o arquivo físico. Não há recuperação."
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPurgeTarget(null);
                setPurgeWord('');
              }}
            >
              Cancelar
            </Button>
            <Button variant="danger" size="sm" icon="trash" disabled={purgeWord !== 'PURGAR'} onClick={doPurge}>
              Purgar
            </Button>
          </>
        }
      >
        <p style={{ fontSize: 13.5, color: 'var(--text-1)', marginBottom: 12 }}>
          Digite <b>PURGAR</b> para confirmar a exclusão permanente de{' '}
          <b>{purgeTarget?.descricao ?? purgeTarget?.id}</b>.
        </p>
        <input
          value={purgeWord}
          onChange={(e) => setPurgeWord(e.target.value)}
          placeholder="PURGAR"
          className="focusable"
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: 14,
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--bg-0)',
            color: 'var(--text-0)',
            outline: 'none'
          }}
        />
      </Modal>

      {/* Modal de exclusão em massa (RN24 / flows §4.4) */}
      <Modal
        open={bulkDeleteOpen}
        onClose={() => {
          setBulkDeleteOpen(false);
          setBulkWord('');
        }}
        title={`Excluir ${selected.size} arquivo(s)`}
        danger
        subtitle="Vão para a lixeira (restauráveis por 30 dias)."
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setBulkDeleteOpen(false);
                setBulkWord('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon="trash"
              disabled={bulkWord !== 'EXCLUIR'}
              onClick={() => {
                setBulkDeleteOpen(false);
                const w = bulkWord;
                setBulkWord('');
                bulkRun('delete', w);
              }}
            >
              Excluir {selected.size}
            </Button>
          </>
        }
      >
        <p style={{ fontSize: 13.5, color: 'var(--text-1)', marginBottom: 12 }}>
          Digite <b>EXCLUIR</b> para confirmar a exclusão de <b>{selected.size}</b> arquivo(s).
          {selected.size > 5 && ' Processa em background.'}
        </p>
        <input
          value={bulkWord}
          onChange={(e) => setBulkWord(e.target.value)}
          placeholder="EXCLUIR"
          className="focusable"
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: 14,
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--bg-0)',
            color: 'var(--text-0)',
            outline: 'none'
          }}
        />
      </Modal>
    </>
  );
}

const thStyle: CSSProperties = {
  padding: '11px 16px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-2)'
};

const dtStyle: CSSProperties = { margin: 0, color: 'var(--text-3)', fontWeight: 500 };

const openLinkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '5px 10px',
  fontSize: 12.5,
  fontWeight: 500,
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-2)',
  color: 'var(--text-1)',
  textDecoration: 'none',
  verticalAlign: 'middle'
};

interface SegOpt {
  value: string;
  label: string;
}

function SegmentedFilter({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: SegOpt[];
}) {
  return (
    <div
      style={{
        display: 'inline-flex',
        background: 'var(--bg-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-sm)',
        padding: 2,
        gap: 2
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              fontSize: 12.5,
              fontWeight: 500,
              borderRadius: 4,
              border: 'none',
              background: active ? 'var(--bg-0)' : 'transparent',
              color: active ? 'var(--text-0)' : 'var(--text-2)',
              boxShadow: active ? 'var(--shadow-1)' : 'none',
              transition: 'all 0.14s var(--ease)',
              cursor: 'pointer'
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
